export type ResersBrandId = "msb" | "smk";

export type ResersRetailerId = "ahold" | "kroger" | "absco" | "stater-bros";

export type ResersConversionMetric = "conversions" | "units";

export interface ResersBrand {
  id: ResersBrandId;
  name: string;
  shortName: string;
  color: string;
  accentColor: string;
}

export interface ResersRetailer {
  id: ResersRetailerId;
  name: string;
  shortName: string;
  color: string;
}

export interface ResersUploadSource {
  retailerId: ResersRetailerId;
  retailerName: string;
  fileName: string;
  brandIds: ResersBrandId[];
}

export interface ResersMonthlyMetric {
  brandId: ResersBrandId;
  brandName: string;
  retailerId: ResersRetailerId;
  retailerName: string;
  month: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  units: number;
  conversions: number | null;
  conversionMetric: ResersConversionMetric;
  sourceFiles: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
}

export interface ResersMetricTotals {
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  units: number;
  conversions: number | null;
  conversionMetric: ResersConversionMetric;
  conversionValue: number;
  conversionLabel: "Conversions" | "Units";
  conversionRateLabel: "Conversion Rate" | "Unit Rate";
  costPerConversionLabel: "Cost / Conversion" | "Cost / Unit";
  ctr: number;
  conversionRate: number;
  costPerConversion: number;
  roas: number;
  cpc: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  sourceFiles: string[];
}

export interface ResersRetailerMixItem {
  retailerId: ResersRetailerId;
  retailerName: string;
  color: string;
  sales: number;
  units: number;
  spend: number;
  roas: number;
  percentageOfSales: number;
  percentageOfUnits: number;
}

export const RESERS_BRANDS: ResersBrand[] = [
  {
    id: "msb",
    name: "Main St Bistro",
    shortName: "MSB",
    color: "#9F1239",
    accentColor: "#F59E0B",
  },
  {
    id: "smk",
    name: "Stonemill Kitchens",
    shortName: "SMK",
    color: "#A16207",
    accentColor: "#0F766E",
  },
];

export const RESERS_RETAILERS: ResersRetailer[] = [
  {
    id: "ahold",
    name: "AHOLD",
    shortName: "AHOLD",
    color: "#1D4ED8",
  },
  {
    id: "kroger",
    name: "Kroger",
    shortName: "Kroger",
    color: "#004990",
  },
  {
    id: "absco",
    name: "Albertsons (ABSCO)",
    shortName: "ABSCO",
    color: "#F97316",
  },
  {
    id: "stater-bros",
    name: "Stater Bros.",
    shortName: "Stater",
    color: "#D71920",
  },
];

export const RESERS_METRIC_COLORS = {
  sales: "#0F766E",
  spend: "#2563EB",
} as const;

export const RESERS_UPLOAD_SOURCES: ResersUploadSource[] = [
  {
    retailerId: "ahold",
    retailerName: "AHOLD",
    fileName: "AHOLD_MSB_Q1.csv",
    brandIds: ["msb"],
  },
  {
    retailerId: "kroger",
    retailerName: "Kroger",
    fileName: "Kroger_MSB_SMK_Q1.csv",
    brandIds: ["msb", "smk"],
  },
  {
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    fileName: "ABSCO_MSB_SMK_Q1.csv",
    brandIds: ["msb", "smk"],
  },
  {
    retailerId: "stater-bros",
    retailerName: "Stater Bros.",
    fileName: "StaterBros_SMK_Q1.csv",
    brandIds: ["smk"],
  },
];

export const RESERS_Q1_MONTHLY_METRICS: ResersMonthlyMetric[] = [
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "ahold",
    retailerName: "AHOLD",
    month: "2026-01",
    impressions: 200403,
    clicks: 4390,
    spend: 3589.51,
    sales: 12845.98,
    units: 2645,
    conversions: 2163,
    conversionMetric: "conversions",
    sourceFiles: ["AHOLD_MSB_Q1.csv"],
    dateRangeStart: "2026-01-01",
    dateRangeEnd: "2026-01-31",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "ahold",
    retailerName: "AHOLD",
    month: "2026-02",
    impressions: 215332,
    clicks: 5016,
    spend: 4146.65,
    sales: 14603.48,
    units: 3096,
    conversions: 2576,
    conversionMetric: "conversions",
    sourceFiles: ["AHOLD_MSB_Q1.csv"],
    dateRangeStart: "2026-02-01",
    dateRangeEnd: "2026-02-28",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "ahold",
    retailerName: "AHOLD",
    month: "2026-03",
    impressions: 187329,
    clicks: 4361,
    spend: 3464.54,
    sales: 12773.13,
    units: 2609,
    conversions: 2227,
    conversionMetric: "conversions",
    sourceFiles: ["AHOLD_MSB_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-31",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-01",
    impressions: 876485,
    clicks: 4209,
    spend: 2500,
    sales: 12863.65,
    units: 2566,
    conversions: 2152,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-01-01",
    dateRangeEnd: "2026-01-01",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-02",
    impressions: 1035702,
    clicks: 5661,
    spend: 3333,
    sales: 17756.1,
    units: 3464,
    conversions: 2892,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-02-01",
    dateRangeEnd: "2026-02-01",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-03",
    impressions: 1399283,
    clicks: 8729,
    spend: 7500,
    sales: 24872.28,
    units: 5131,
    conversions: 4280,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-01",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-01",
    impressions: 1905701,
    clicks: 4549,
    spend: 2499.9,
    sales: 19383.22,
    units: 3947,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-01-06",
    dateRangeEnd: "2026-01-31",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-02",
    impressions: 1542098,
    clicks: 4468,
    spend: 2500,
    sales: 23076.97,
    units: 4744,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-02-01",
    dateRangeEnd: "2026-02-28",
  },
  {
    brandId: "msb",
    brandName: "Main St Bistro",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-03",
    impressions: 6345213,
    clicks: 12227,
    spend: 7500.04,
    sales: 56855.34,
    units: 11670,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-31",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-01",
    impressions: 778476,
    clicks: 2984,
    spend: 2499.64,
    sales: 8550.54,
    units: 1676,
    conversions: 1542,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-01-01",
    dateRangeEnd: "2026-01-01",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-02",
    impressions: 1221837,
    clicks: 4290,
    spend: 3333,
    sales: 12429.33,
    units: 2497,
    conversions: 2304,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-02-01",
    dateRangeEnd: "2026-02-01",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "kroger",
    retailerName: "Kroger",
    month: "2026-03",
    impressions: 1685195,
    clicks: 4074,
    spend: 3333,
    sales: 13438.47,
    units: 2433,
    conversions: 2281,
    conversionMetric: "conversions",
    sourceFiles: ["Kroger_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-01",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-01",
    impressions: 22929,
    clicks: 11,
    spend: 16.5,
    sales: 9.98,
    units: 2,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-01-05",
    dateRangeEnd: "2026-01-31",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-02",
    impressions: 23,
    clicks: 0,
    spend: 0,
    sales: 0,
    units: 0,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-02-04",
    dateRangeEnd: "2026-02-28",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "absco",
    retailerName: "Albertsons (ABSCO)",
    month: "2026-03",
    impressions: 600390,
    clicks: 635,
    spend: 1571.16,
    sales: 1964.84,
    units: 372,
    conversions: null,
    conversionMetric: "units",
    sourceFiles: ["ABSCO_MSB_SMK_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-31",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "stater-bros",
    retailerName: "Stater Bros.",
    month: "2026-01",
    impressions: 29637,
    clicks: 77,
    spend: 81.0182,
    sales: 69.86,
    units: 14,
    conversions: 13,
    conversionMetric: "conversions",
    sourceFiles: ["StaterBros_SMK_Q1.csv"],
    dateRangeStart: "2026-01-01",
    dateRangeEnd: "2026-01-31",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "stater-bros",
    retailerName: "Stater Bros.",
    month: "2026-02",
    impressions: 11807,
    clicks: 327,
    spend: 326.71667,
    sales: 318.62,
    units: 81,
    conversions: 70,
    conversionMetric: "conversions",
    sourceFiles: ["StaterBros_SMK_Q1.csv"],
    dateRangeStart: "2026-02-01",
    dateRangeEnd: "2026-02-28",
  },
  {
    brandId: "smk",
    brandName: "Stonemill Kitchens",
    retailerId: "stater-bros",
    retailerName: "Stater Bros.",
    month: "2026-03",
    impressions: 11110,
    clicks: 318,
    spend: 346.54896,
    sales: 489.02,
    units: 100,
    conversions: 93,
    conversionMetric: "conversions",
    sourceFiles: ["StaterBros_SMK_Q1.csv"],
    dateRangeStart: "2026-03-01",
    dateRangeEnd: "2026-03-31",
  },
];

const emptyTotals = (): ResersMetricTotals => ({
  impressions: 0,
  clicks: 0,
  spend: 0,
  sales: 0,
  units: 0,
  conversions: null,
  conversionMetric: "units",
  conversionValue: 0,
  conversionLabel: "Units",
  conversionRateLabel: "Unit Rate",
  costPerConversionLabel: "Cost / Unit",
  ctr: 0,
  conversionRate: 0,
  costPerConversion: 0,
  roas: 0,
  cpc: 0,
  dateRangeStart: "",
  dateRangeEnd: "",
  sourceFiles: [],
});

const ratio = (numerator: number, denominator: number) =>
  denominator === 0 ? 0 : numerator / denominator;

export const getBrand = (brandId: ResersBrandId) =>
  RESERS_BRANDS.find((brand) => brand.id === brandId) ?? RESERS_BRANDS[0];

export const getRetailer = (retailerId: ResersRetailerId) =>
  RESERS_RETAILERS.find((retailer) => retailer.id === retailerId) ??
  RESERS_RETAILERS[0];

export const getBrandRecords = (brandId: ResersBrandId) =>
  RESERS_Q1_MONTHLY_METRICS.filter((record) => record.brandId === brandId);

export const getBrandRetailerRecords = (
  brandId: ResersBrandId,
  retailerId: ResersRetailerId,
) =>
  RESERS_Q1_MONTHLY_METRICS.filter(
    (record) => record.brandId === brandId && record.retailerId === retailerId,
  );

export const getRetailersForBrand = (brandId: ResersBrandId) =>
  RESERS_RETAILERS.filter((retailer) =>
    RESERS_Q1_MONTHLY_METRICS.some(
      (record) => record.brandId === brandId && record.retailerId === retailer.id,
    ),
  );

export const aggregateMetrics = (
  records: ResersMonthlyMetric[],
): ResersMetricTotals => {
  if (records.length === 0) return emptyTotals();

  const conversionsAvailable = records.every(
    (record) => record.conversions !== null,
  );
  const conversions = conversionsAvailable
    ? records.reduce((sum, record) => sum + (record.conversions ?? 0), 0)
    : null;

  const totals = records.reduce(
    (accumulator, record) => ({
      impressions: accumulator.impressions + record.impressions,
      clicks: accumulator.clicks + record.clicks,
      spend: accumulator.spend + record.spend,
      sales: accumulator.sales + record.sales,
      units: accumulator.units + record.units,
    }),
    {
      impressions: 0,
      clicks: 0,
      spend: 0,
      sales: 0,
      units: 0,
    },
  );

  const conversionValue = conversionsAvailable ? conversions ?? 0 : totals.units;
  const conversionMetric: ResersConversionMetric = conversionsAvailable
    ? "conversions"
    : "units";

  return {
    ...totals,
    spend: Math.round(totals.spend * 100) / 100,
    sales: Math.round(totals.sales * 100) / 100,
    conversions,
    conversionMetric,
    conversionValue,
    conversionLabel: conversionsAvailable ? "Conversions" : "Units",
    conversionRateLabel: conversionsAvailable ? "Conversion Rate" : "Unit Rate",
    costPerConversionLabel: conversionsAvailable
      ? "Cost / Conversion"
      : "Cost / Unit",
    ctr: ratio(totals.clicks, totals.impressions),
    conversionRate: ratio(conversionValue, totals.clicks),
    costPerConversion: ratio(totals.spend, conversionValue),
    roas: ratio(totals.sales, totals.spend),
    cpc: ratio(totals.spend, totals.clicks),
    dateRangeStart: records
      .map((record) => record.dateRangeStart)
      .sort((a, b) => a.localeCompare(b))[0],
    dateRangeEnd: records
      .map((record) => record.dateRangeEnd)
      .sort((a, b) => b.localeCompare(a))[0],
    sourceFiles: Array.from(
      new Set(records.flatMap((record) => record.sourceFiles)),
    ).sort((a, b) => a.localeCompare(b)),
  };
};

export const getBrandTotals = (brandId: ResersBrandId) =>
  aggregateMetrics(getBrandRecords(brandId));

export const getBrandMonthlyTotals = (brandId: ResersBrandId) => {
  const monthSet = new Set(
    getBrandRecords(brandId).map((record) => record.month),
  );

  return Array.from(monthSet)
    .sort((a, b) => a.localeCompare(b))
    .map((month) => {
      const records = getBrandRecords(brandId).filter(
        (record) => record.month === month,
      );
      return {
        month,
        totals: aggregateMetrics(records),
      };
    });
};

export const getRetailerMixForBrand = (
  brandId: ResersBrandId,
): ResersRetailerMixItem[] => {
  const brandTotals = getBrandTotals(brandId);

  return getRetailersForBrand(brandId)
    .map((retailer) => {
      const records = getBrandRetailerRecords(brandId, retailer.id);
      const totals = aggregateMetrics(records);

      return {
        retailerId: retailer.id,
        retailerName: retailer.name,
        color: retailer.color,
        sales: totals.sales,
        units: totals.units,
        spend: totals.spend,
        roas: totals.roas,
        percentageOfSales: ratio(totals.sales, brandTotals.sales),
        percentageOfUnits: ratio(totals.units, brandTotals.units),
      };
    })
    .sort((a, b) => b.sales - a.sales);
};
